import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import { Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import {
  ThemeScope,
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Separator,
} from "./ui/shadcn";

/* ------------------------------------------------------------------ */
/*  Design tokens (shadcn-style) : emerald finance theme               */
/* ------------------------------------------------------------------ */
const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": "#062a21",
  "--card": "#ffffff",
  "--card-foreground": "#062a21",
  "--primary": "#047857",
  "--primary-foreground": "#ffffff",
  "--secondary": "#ecfdf5",
  "--secondary-foreground": "#065f46",
  "--muted": "#f3faf6",
  "--muted-foreground": "#4d6b5f",
  "--accent": "#ecfdf5",
  "--destructive": "#b91c1c",
  "--destructive-foreground": "#ffffff",
  "--border": "#d6eadf",
  "--input": "#cfe3d9",
  "--ring": "#10b981",
  "--radius": "0.875rem",
};

const EASE = [0.22, 1, 0.36, 1];
const MotionButton = motion.create(Button);


/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
.ld5-font, .ld5-font * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }
.ld5-root { display: flex; width: 100%; height: 100vh; overflow: hidden; position: relative; background: #ffffff; }
@supports (height: 100dvh) { .ld5-root { height: 100dvh; } }
.ld5-num { font-variant-numeric: tabular-nums; }

@keyframes ld5Float { 0%, 100% { transform: translate3d(0,0,0) scale(1); } 50% { transform: translate3d(-30px,24px,0) scale(1.07); } }
@keyframes ld5Pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,.6); } 50% { box-shadow: 0 0 0 6px rgba(52,211,153,0); } }
@keyframes ld5Sheen { from { transform: translateX(-120%) skewX(-20deg); } to { transform: translateX(320%) skewX(-20deg); } }

/* ---------- LEFT : calm brand panel ---------- */
.ld5-left { display: none; position: relative; overflow: hidden; flex-shrink: 0; width: 58%; flex-direction: column; gap: clamp(28px, 6vh, 56px);
  padding: clamp(36px, 5.5vh, 64px) clamp(40px, 4.5vw, 80px); border-radius: 0 28px 28px 0; z-index: 2;
  background: linear-gradient(155deg, #ecfdf5 0%, #d1fae5 55%, #a7f3d0 130%); justify-content: center; }
.ld5-glow { position: absolute; border-radius: 50%; pointer-events: none; }
.ld5-glow-1 { top: -18%; left: -12%; width: 560px; height: 560px; background: radial-gradient(circle, rgba(16,185,129,.22) 0%, transparent 68%); animation: ld5Float 18s ease-in-out infinite; }
.ld5-glow-2 { bottom: -22%; right: -12%; width: 700px; height: 700px; background: radial-gradient(circle, rgba(5,150,105,.16) 0%, transparent 68%); animation: ld5Float 22s ease-in-out infinite reverse; }
.ld5-grid { position: absolute; inset: 0; pointer-events: none;
  background-image: linear-gradient(rgba(4,120,87,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(4,120,87,.07) 1px, transparent 1px);
  background-size: 44px 44px;
  -webkit-mask-image: radial-gradient(ellipse at 70% 75%, #000 0%, transparent 72%); mask-image: radial-gradient(ellipse at 70% 75%, #000 0%, transparent 72%); }
.ld5-left-logo { margin-bottom: 8px; }
.ld5-left-logo { position: relative; z-index: 3; }
.ld5-left-logo img { height: 46px; display: block; }
.ld5-hero { position: relative; z-index: 3; }
.ld5-root .ld5-pill { gap: 8px; padding: 6px 14px; font-size: .68rem; letter-spacing: .18em; text-transform: uppercase; font-weight: 700; color: #065f46; border-color: rgba(4,120,87,.3); background: rgba(255,255,255,.7); }
.ld5-dot { width: 7px; height: 7px; border-radius: 50%; background: #059669; animation: ld5Pulse 2s ease-in-out infinite; }
.ld5-h { margin: 20px 0 0; font-size: clamp(2.2rem, 1.2rem + 1.9vw, 3.4rem); font-weight: 800; line-height: 1.08; letter-spacing: -.03em; color: #062a21; }
.ld5-grad { background: linear-gradient(90deg, #047857 0%, #10b981 100%); -webkit-background-clip: text; background-clip: text; color: transparent; -webkit-text-fill-color: transparent; }
.ld5-sub { margin: 18px 0 0; max-width: 440px; color: #355a4c; font-size: 1rem; line-height: 1.75; }
.ld5-feats { list-style: none; margin: 22px 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 8px 10px; max-width: 500px; }
.ld5-feats li { display: flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 99px; font-size: .8rem; font-weight: 600; color: #065f46; background: rgba(255,255,255,.7); border: 1px solid rgba(4,120,87,.2); }
.ld5-feats svg { font-size: 15px; color: #059669; }


/* ---------- RIGHT : form ---------- */
.ld5-right { flex: 1; min-width: 0; height: 100%; display: flex; flex-direction: column; position: relative; overflow-x: hidden; overflow-y: auto; background: #eef6f2; }
.ld5-band { position: relative; overflow: hidden; flex-shrink: 0; padding: 28px 20px 78px; border-radius: 0 0 28px 28px; background: linear-gradient(155deg, #ecfdf5 0%, #d1fae5 100%); }
.ld5-band .ld5-glow-1 { width: 320px; height: 320px; top: -40%; left: -10%; }
.ld5-band-in { position: relative; z-index: 1; max-width: 440px; margin: 0 auto; }
.ld5-band-in img { display: block; height: 34px; }
.ld5-band-tag { margin: 14px 0 0; max-width: 80%; color: #065f46; font-size: .95rem; font-weight: 600; line-height: 1.4; }
.ld5-wrap { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 0 16px 8px; }
.ld5-form { width: 100%; max-width: 440px; margin: -52px 0 0; position: relative; z-index: 1; }
.ld5-root .ld5-form-card { border-radius: 22px; border-color: rgba(6,78,59,.08); box-shadow: 0 24px 50px -16px rgba(2,44,34,.28); overflow: hidden; }
.ld5-form-card .ui-card-header { padding: 26px 22px 6px; }
.ld5-form-card .ui-card-content { padding: 14px 22px 6px; }
.ld5-form-card .ui-card-footer { padding: 6px 22px 22px; flex-direction: column; align-items: stretch; gap: 14px; }
.ld5-hdr { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; }
.ld5-root .ld5-eyebrow { gap: 6px; padding: 4px 12px; font-size: .68rem; letter-spacing: .14em; text-transform: uppercase; font-weight: 800; color: #047857; background: #d1fae5; }
.ld5-eyebrow svg { font-size: 14px; }
.ld5-root .ld5-title { margin: 0; font-size: clamp(1.55rem, 1.2rem + 1.5vw, 2.1rem); font-weight: 800; letter-spacing: -.04em; line-height: 1.12; color: #062a21; overflow-wrap: break-word; }
.ld5-root .ld5-desc { margin: 0; font-size: .9rem; line-height: 1.55; color: #4d6b5f; }
.ld5-field { margin-bottom: 6px; }
.ld5-lrow { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.ld5-root .ld5-label { font-size: .82rem; font-weight: 700; color: #062a21; line-height: 1.2; }
.ld5-link { color: #047857; font-size: .82rem; font-weight: 700; text-decoration: none; border-radius: 4px; }
.ld5-link:hover { text-decoration: underline; text-underline-offset: 3px; }
.ld5-link:focus-visible { outline: 2px solid #10b981; outline-offset: 3px; }
.ld5-iwrap { position: relative; }
.ld5-ic { position: absolute; z-index: 1; left: 14px; top: 50%; transform: translateY(-50%); display: flex; color: #5f7d70; pointer-events: none; transition: color .2s ease; }
.ld5-ic svg { font-size: 20px; }
.ld5-iwrap:focus-within .ld5-ic { color: #059669; }
.ld5-root .ld5-input { height: 3.4rem; min-height: 3.4rem; padding: 0 14px 0 44px; font-size: 1rem; border-radius: 12px; border: 1.5px solid #6f9a88; background-color: #f1f8f4; color: #062a21; -webkit-text-fill-color: #062a21; caret-color: #047857; opacity: 1; transition: background-color .2s ease, border-color .2s ease, box-shadow .2s ease; }
.ld5-root .ld5-input:hover { border-color: #059669; }
.ld5-root .ld5-input:focus, .ld5-root .ld5-input:focus-visible { background-color: #ffffff; border-color: #047857; box-shadow: 0 0 0 4px rgba(16,185,129,.22); }
.ld5-root .ld5-input::placeholder { color: #55746a; -webkit-text-fill-color: #55746a; opacity: 1; }
.ld5-root .ld5-input:-webkit-autofill, .ld5-root .ld5-input:-webkit-autofill:hover { -webkit-text-fill-color: #062a21; caret-color: #047857; -webkit-box-shadow: 0 0 0 1000px #f1f8f4 inset; box-shadow: 0 0 0 1000px #f1f8f4 inset; transition: background-color 9999s ease-out 0s; }
.ld5-root .ld5-input:-webkit-autofill:focus { -webkit-text-fill-color: #062a21; caret-color: #047857; -webkit-box-shadow: 0 0 0 1000px #ffffff inset, 0 0 0 4px rgba(16,185,129,.22); box-shadow: 0 0 0 1000px #ffffff inset, 0 0 0 4px rgba(16,185,129,.22); transition: background-color 9999s ease-out 0s; }
.ld5-root .ld5-input.has-end { padding-right: 54px; }
.ld5-root .ld5-input.has-end2 { padding-right: 90px; }
.ld5-end { position: absolute; z-index: 1; right: 6px; top: 50%; transform: translateY(-50%); display: flex; align-items: center; gap: 2px; }
.ld5-end-inert { pointer-events: none; }
.ld5-caps-ic { color: #d97706; font-size: 20px; margin-right: 6px; }
.ld5-root .ld5-eye { height: 44px; width: 44px; min-width: 44px; border-radius: 10px; color: #4d6b5f; }
.ld5-root .ld5-eye:hover { background: #e3f4ec; color: #047857; }
.ld5-help { min-height: 20px; margin: 6px 4px 0; display: flex; align-items: center; gap: 5px; font-size: .75rem; font-weight: 600; color: #b45309; }
.ld5-help svg { font-size: 14px; }
.ld5-hrow { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.ld5-hrow .ld5-help { flex: 1; min-width: 0; }
.ld5-forgot { flex-shrink: 0; display: inline-flex; align-items: center; min-height: 44px; margin: -4px -6px -8px 0; padding: 0 6px; }
.ld5-fmeta { margin-bottom: 16px; }
.ld5-root .ld5-alert { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 16px; padding: 12px 14px; border-radius: 12px; font-size: .84rem; font-weight: 600; line-height: 1.45; background: #fef2f2; border-color: #fecaca; color: #b91c1c; }
.ld5-alert svg { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.ld5-alert span { min-width: 0; overflow-wrap: anywhere; }
.ld5-form, .ld5-form-card, .ld5-form-card form { min-width: 0; }
.ld5-root .ld5-desc { overflow-wrap: break-word; }
.ld5-root .ld5-submit { position: relative; overflow: hidden; width: 100%; height: 3.4rem; border-radius: 12px; font-size: .98rem; font-weight: 700; letter-spacing: .01em; background: linear-gradient(135deg, #047857 0%, #065f46 100%); box-shadow: 0 10px 24px -8px rgba(4,120,87,.55); }
.ld5-root .ld5-submit:hover { opacity: 1; box-shadow: 0 16px 30px -10px rgba(4,120,87,.65); }
.ld5-root .ld5-submit:disabled { opacity: 1; background: #4a9a82; box-shadow: none; color: #fff; }
.ld5-root .ld5-submit::after { content: ''; position: absolute; top: 0; left: 0; width: 28%; height: 100%; background: linear-gradient(110deg, transparent, rgba(255,255,255,.32), transparent); transform: translateX(-120%) skewX(-20deg); }
.ld5-root .ld5-submit:hover:not(:disabled)::after { animation: ld5Sheen .8s ease; }
.ld5-submit svg { font-size: 20px; }
.ld5-root .ld5-bio { width: 100%; height: 3.1rem; margin-top: 12px; border-radius: 12px; border-color: #a7f3d0; color: #047857; font-weight: 700; }
.ld5-root .ld5-bio:hover { background: #ecfdf5; }
.ld5-signup { margin: 0; padding: 14px; border-radius: 12px; text-align: center; font-size: .875rem; color: #4d6b5f; background: #f3faf6; border: 1px solid #d6eadf; }
.ld5-signup .ld5-link { font-size: .875rem; display: inline-block; padding: 12px 4px; margin: -12px 0; }
.ld5-foot { flex-shrink: 0; margin: 0; padding: 18px 16px 20px; text-align: center; font-size: .75rem; color: #4d6b5f; position: relative; z-index: 1; }
.ld5-foot svg { font-size: 12px; vertical-align: middle; margin-right: 3px; }

/* company selection */
.ld5-ohead { display: flex; align-items: center; gap: 14px; min-width: 0; }
.ld5-ohead .ld5-hdr { min-width: 0; flex: 1; gap: 6px; }
.ld5-obadge { width: 46px; height: 46px; flex-shrink: 0; border-radius: 14px; display: grid; place-items: center; color: #fff; background: linear-gradient(135deg, #10b981, #047857); box-shadow: 0 10px 20px -8px rgba(4,120,87,.6); }
.ld5-colist { display: flex; flex-direction: column; gap: 10px; margin: 0 -4px 18px; padding: 4px; max-height: min(46vh, 340px); overflow-y: auto; overflow-x: hidden; list-style: none; min-width: 0; }
@supports (height: 100dvh) { .ld5-colist { max-height: min(46dvh, 340px); } }
.ld5-co { display: block; position: relative; cursor: pointer; }
.ld5-sr { position: absolute; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0,0,0,0); border: 0; opacity: 0; }
.ld5-root .ld5-co-card { display: flex; align-items: center; gap: 14px; min-width: 0; min-height: 64px; padding: 12px 16px; border-radius: 14px; border: 1.5px solid var(--border); background: #fff; box-shadow: none; transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease; }
.ld5-co:hover .ld5-co-card { border-color: #34d399; }
.ld5-co.is-on .ld5-co-card { border-color: #059669; background: #f0fdf8; box-shadow: 0 0 0 4px rgba(16,185,129,.14); }
.ld5-sr:focus-visible + .ld5-co-card { outline: 2px solid #059669; outline-offset: 2px; }
.ld5-av { width: 42px; height: 42px; flex-shrink: 0; border-radius: 12px; display: grid; place-items: center; font-weight: 800; color: #065f46; background: linear-gradient(135deg, #d1fae5, #a7f3d0); transition: background .2s ease, color .2s ease; }
.ld5-co.is-on .ld5-av { color: #fff; background: linear-gradient(135deg, #059669, #047857); }
.ld5-conm { min-width: 0; font-size: .92rem; font-weight: 700; color: #062a21; overflow-wrap: anywhere; }
.ld5-tick { margin-left: auto; width: 24px; height: 24px; flex-shrink: 0; border-radius: 50%; display: grid; place-items: center; color: #fff; border: 2px solid #b8d6c8; transition: background-color .2s ease, border-color .2s ease; }
.ld5-tick svg { font-size: 14px; opacity: 0; transition: opacity .2s ease; }
.ld5-co.is-on .ld5-tick { background: #059669; border-color: #059669; }
.ld5-co.is-on .ld5-tick svg { opacity: 1; }
.ld5-root .ld5-go { width: 100%; height: 3.4rem; border-radius: 12px; font-size: .98rem; font-weight: 700; background: linear-gradient(135deg, #047857 0%, #065f46 100%); box-shadow: 0 10px 24px -8px rgba(4,120,87,.55); }
.ld5-root .ld5-go:hover { opacity: 1; box-shadow: 0 16px 30px -10px rgba(4,120,87,.65); }

/* session dialog */
.ld5-sbanner { padding: 22px clamp(16px, 5vw, 24px) 18px; color: #fff; background: linear-gradient(135deg, #022c22 0%, #047857 100%); }
.ld5-sbtop { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.ld5-sicon { width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; display: grid; place-items: center; background: rgba(255,255,255,.16); }
.ld5-stitle { margin: 0; font-size: 1.05rem; font-weight: 800; line-height: 1.3; }
.ld5-ssub { margin: 2px 0 0; font-size: .78rem; color: rgba(255,255,255,.78); }
.ld5-snote { margin: 0; padding: 8px 14px; border-radius: 10px; background: rgba(255,255,255,.13); font-size: .8rem; line-height: 1.5; color: rgba(255,255,255,.9); }
.ld5-slist { padding: 16px clamp(14px, 4.5vw, 20px) 6px; }
.ld5-sh { margin: 0 0 10px; font-size: .72rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #4d6b5f; }
.ld5-root .ld5-sess, .ld5-sess { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 12px; min-width: 0; margin-bottom: 8px; padding: 12px; border-radius: 14px; background: #f6faf8; transition: border-color .2s ease, box-shadow .2s ease; }
.ld5-sess:hover { border-color: #10b981; box-shadow: 0 6px 14px -6px rgba(5,150,105,.3); }
.ld5-sdev { width: 38px; height: 38px; flex-shrink: 0; border-radius: 11px; display: grid; place-items: center; background: #d1fae5; }
.ld5-sinfo { flex: 1 1 130px; min-width: 0; }
.ld5-sname { margin: 0; font-size: .87rem; font-weight: 700; color: #062a21; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ld5-smeta { margin: 3px 0 0; display: flex; align-items: center; flex-wrap: wrap; gap: 4px; font-size: .73rem; color: #4d6b5f; }
.ld5-smeta svg { font-size: 12px; }
.ld5-sact { padding: 4px clamp(14px, 4.5vw, 20px) 20px; }
.ld5-root .ld5-kick, .ld5-kick { min-width: 88px; min-height: 44px; flex-shrink: 0; margin-left: auto; font-size: .78rem; font-weight: 700; }
.ld5-scancel { width: 100%; min-height: 44px; color: #4d6b5f; font-weight: 600; }
.ld5-smeta { overflow-wrap: anywhere; }

/* ---------- breakpoints ---------- */
@media (max-width: 380px) {
  .ld5-wrap { padding: 0 12px 8px; }
  .ld5-form-card .ui-card-header { padding: 22px 18px 6px; }
  .ld5-form-card .ui-card-content { padding: 12px 18px 6px; }
  .ld5-form-card .ui-card-footer { padding: 6px 18px 18px; }
  .ld5-band { padding: 24px 16px 74px; }
}
@media (min-width: 600px) {
  .ld5-wrap { padding: 0 32px 8px; }
  .ld5-form-card .ui-card-header { padding: 32px 32px 6px; }
  .ld5-form-card .ui-card-content { padding: 16px 32px 6px; }
  .ld5-form-card .ui-card-footer { padding: 6px 32px 30px; }
  .ld5-band { padding: 32px 32px 82px; }
}
@media (min-width: 768px) {
  .ld5-form, .ld5-band-in { max-width: 480px; }
  .ld5-band-in img { height: 40px; }
  .ld5-band-tag { font-size: 1.05rem; }
}
/* landscape phones / very short screens below lg: compact band so the form comes first */
@media (max-width: 1199px) and (max-height: 520px) {
  .ld5-band { padding: 14px 20px 44px; border-radius: 0 0 20px 20px; }
  .ld5-band-in img { height: 28px; }
  .ld5-band-tag { display: none; }
  .ld5-form { margin-top: -28px; }
  .ld5-form-card .ui-card-header { padding-top: 18px; }
  .ld5-hdr { gap: 6px; }
  .ld5-foot { padding-top: 12px; padding-bottom: 14px; }
}
@media (min-width: 1200px) {
  .ld5-left { display: flex; }
  .ld5-right { background: radial-gradient(ellipse at 85% 8%, rgba(16,185,129,.08) 0%, transparent 50%), radial-gradient(ellipse at 15% 95%, rgba(5,150,105,.06) 0%, transparent 50%), #ffffff; }
  .ld5-band { display: none; }
  .ld5-wrap { justify-content: center; padding: 0 48px; }
  .ld5-form { margin: auto 0; max-width: 400px; }
  .ld5-root .ld5-form-card { border: 0; border-radius: 0; background: transparent; box-shadow: none; overflow: visible; }
  .ld5-form-card .ui-card-header { padding: 0 0 8px; }
  .ld5-form-card .ui-card-content { padding: 18px 0 4px; }
  .ld5-form-card .ui-card-footer { padding: 8px 0 0; }
  .ld5-foot { padding-bottom: 24px; }
}
@media (min-width: 1536px) {
  .ld5-left-logo img { height: 52px; }
  .ld5-wrap { padding: 0 64px; }
  .ld5-form { max-width: 440px; }
}
@media (max-height: 760px) { .ld5-feats { display: none; } }
@media (max-height: 640px) { .ld5-sub { display: none; } }

@media (prefers-reduced-motion: reduce) {
  .ld5-glow, .ld5-dot, .ld5-submit::after { animation: none !important; }
}
`;


/* ------------------------------------------------------------------ */
/*  Login component                                                    */
/* ------------------------------------------------------------------ */
function LoginDesign05({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reduce = useReducedMotion();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#059669" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#059669" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#059669" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#059669" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ---- motion helpers (visual only) ----
  // The step wrapper never starts hidden (initial={false}); it only fades out on exit.
  const stepMotion = {
    initial: false,
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: reduce ? 0 : -8, transition: { duration: reduce ? 0.01 : 0.18 } },
  };
  const rise = (delay = 0) => ({
    initial: reduce ? false : { opacity: 1, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.65, delay, ease: EASE },
  });
  // Transform-only entrance for inputs / buttons: opacity is never touched, so they can never get stuck invisible.
  const slide = (delay = 0) => ({
    initial: reduce ? false : { y: 14 },
    animate: { y: 0 },
    transition: { duration: 0.5, delay, ease: EASE },
  });
  const press = reduce ? {} : { whileHover: { y: -2 }, whileTap: { scale: 0.98 } };

  const features = [
    "Multi-company",
    "GST-ready",
    "Audit trail",
  ];

  const capsHelp = capsLockOn ? (
    <>
      <WarningAmberIcon />
      Caps Lock is ON
    </>
  ) : (
    " "
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key={errorMessage}
          initial={{ opacity: 1, y: reduce ? 0 : -6 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -7, 7, -5, 5, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.01 : 0.25, x: { duration: reduce ? 0.01 : 0.45 } }}
        >
          <Alert variant="destructive" className="ld5-alert">
            <WarningAmberIcon />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <ThemeScope vars={THEME_VARS} className="ld5-root ld5-font">
      <style>{CSS}</style>

      {/* ── LEFT : finance dashboard preview ── */}
      <aside className="ld5-left" aria-label="ReonBook product overview">
        <div className="ld5-grid" aria-hidden="true" />
        <div className="ld5-glow ld5-glow-1" aria-hidden="true" />
        <div className="ld5-glow ld5-glow-2" aria-hidden="true" />

        <motion.div className="ld5-left-logo" {...rise(0.05)}>
          <img src="/RBLOGOBLUE.svg" alt="ReonBook" />
        </motion.div>

        <div className="ld5-hero">
          <motion.div {...rise(0.15)}>
            <Badge variant="outline" className="ld5-pill">
              <span className="ld5-dot" aria-hidden="true" />
              Enterprise Accounting Platform
            </Badge>
          </motion.div>
          <motion.p className="ld5-h" {...rise(0.25)}>
            Accounting that
            <br />
            <span className="ld5-grad">feels effortless.</span>
          </motion.p>
          <motion.p className="ld5-sub" {...rise(0.35)}>
            Invoices, GST and multi-company ledgers, together in one calm workspace.
          </motion.p>
          <ul className="ld5-feats">
            {features.map((feat, i) => (
              <motion.li key={feat} {...rise(0.5 + i * 0.08)}>
                <CheckIcon />
                {feat}
              </motion.li>
            ))}
          </ul>
        </div>

      </aside>

      {/* ── RIGHT : form ── */}
      <main className="ld5-right">
        {/* Compact brand band (below lg) */}
        <div className="ld5-band">
          <div className="ld5-glow ld5-glow-1" aria-hidden="true" />
          <div className="ld5-band-in">
            <motion.img src="/RBLOGOBLUE.svg" alt="ReonBook" {...rise(0)} />
            <motion.p className="ld5-band-tag" {...rise(0.1)}>Accounting that feels effortless.</motion.p>
          </div>
        </div>

        <div className="ld5-wrap">
          <div className="ld5-form">
            <AnimatePresence mode="wait">
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div key="login" {...stepMotion}>
                  <Card className="ld5-form-card">
                    <CardHeader>
                      <motion.div className="ld5-hdr" {...rise(0.05)}>
                        <Badge variant="secondary" className="ld5-eyebrow">
                          <LockOutlinedIcon />
                          Secure sign-in
                        </Badge>
                        <CardTitle className="ld5-title" aria-level={1}>Welcome back</CardTitle>
                        <CardDescription className="ld5-desc">Sign in to access your ReonBook dashboard</CardDescription>
                      </motion.div>
                    </CardHeader>

                    <CardContent>
                      <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }} noValidate>

                        <motion.div className="ld5-field" {...slide(0.12)}>
                          <div className="ld5-lrow">
                            <Label htmlFor="ld5-identifier" className="ld5-label">Username, Email or Mobile</Label>
                          </div>
                          <div className="ld5-iwrap">
                            <span className="ld5-ic" aria-hidden="true"><PersonOutlineIcon /></span>
                            <Input
                              id="ld5-identifier"
                              name="identifier"
                              type="text"
                              className={"ld5-input" + (capsLockOn ? " has-end" : "")}
                              placeholder="Username, email or mobile"
                              autoComplete="username"
                              autoCapitalize="none"
                              spellCheck={false}
                              value={identifier}
                              onChange={(e) => setIdentifier(e.target.value)}
                              onKeyUp={handleCapsLockCheck}
                              onFocus={handleCapsLockCheck}
                              onBlur={() => setCapsLockOn(false)}
                              aria-describedby={capsLockOn ? "ld5-caps-id" : undefined}
                            />
                            {capsLockOn && (
                              <span className="ld5-end ld5-end-inert" aria-hidden="true">
                                <WarningAmberIcon className="ld5-caps-ic" sx={{ mr: 0.75 }} />
                              </span>
                            )}
                          </div>
                          <p className="ld5-help" id="ld5-caps-id">{capsHelp}</p>
                        </motion.div>

                        <motion.div className="ld5-field" {...slide(0.19)}>
                          <div className="ld5-lrow">
                            <Label htmlFor="ld5-password" className="ld5-label">Password</Label>
                          </div>
                          <div className="ld5-iwrap">
                            <span className="ld5-ic" aria-hidden="true"><LockOutlinedIcon /></span>
                            <Input
                              id="ld5-password"
                              name="password"
                              type={showPassword ? "text" : "password"}
                              className={"ld5-input " + (capsLockOn ? "has-end2" : "has-end")}
                              placeholder="Enter your password"
                              autoComplete="current-password"
                              value={userPassword}
                              onChange={(e) => setUserPassword(e.target.value)}
                              onKeyUp={handleCapsLockCheck}
                              onFocus={handleCapsLockCheck}
                              onBlur={() => setCapsLockOn(false)}
                              aria-describedby={capsLockOn ? "ld5-caps-pw" : undefined}
                            />
                            <span className="ld5-end">
                              {capsLockOn && <WarningAmberIcon className="ld5-caps-ic" aria-hidden="true" />}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="ld5-eye"
                                onClick={handleTogglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                aria-pressed={showPassword}
                              >
                                {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                              </Button>
                            </span>
                          </div>
                          <div className="ld5-hrow">
                            <p className="ld5-help" id="ld5-caps-pw" aria-live="polite">{capsHelp}</p>
                            <Link to="/forgot-password" className="ld5-link ld5-forgot">Forgot password?</Link>
                          </div>
                        </motion.div>

                        <div className="ld5-fmeta">{errorAlert}</div>

                        <motion.div {...slide(0.26)}>
                          <MotionButton
                            type="submit"
                            size="lg"
                            className="ld5-submit"
                            disabled={isLoading}
                            aria-busy={isLoading}
                            {...press}
                          >
                            {isLoading ? (
                              <>
                                <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                                Signing in...
                              </>
                            ) : (
                              <>
                                Sign In
                                <ArrowForwardIcon />
                              </>
                            )}
                          </MotionButton>

                          {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                            <MotionButton
                              type="button"
                              variant="outline"
                              className="ld5-bio"
                              onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}
                              {...press}
                            >
                              <FingerprintIcon sx={{ fontSize: 20 }} />
                              Login with Biometric
                            </MotionButton>
                          )}
                        </motion.div>
                      </form>
                    </CardContent>

                    <CardFooter>
                      <Separator />
                      <motion.p className="ld5-signup" {...rise(0.34)}>
                        Don't have an account?{" "}
                        <Link to="/register" className="ld5-link">Sign up now</Link>
                      </motion.p>
                    </CardFooter>
                  </Card>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div key="company" {...stepMotion}>
                  <Card className="ld5-form-card">
                    <CardHeader>
                      <motion.div className="ld5-ohead" {...rise(0.05)}>
                        <span className="ld5-obadge" aria-hidden="true"><BusinessOutlinedIcon /></span>
                        <div className="ld5-hdr">
                          <CardTitle className="ld5-title" aria-level={1}>Select Organization</CardTitle>
                          <CardDescription className="ld5-desc">Choose which organization to access</CardDescription>
                        </div>
                      </motion.div>
                    </CardHeader>

                    <CardContent>
                      <motion.div {...slide(0.12)} role="radiogroup" aria-label="Choose your organization" className="ld5-colist">
                        {companies.map((c) => {
                          const isOn = selectedCompany?.companyId === c.companyId;
                          return (
                            <motion.label
                              key={c.companyId}
                              className={"ld5-co" + (isOn ? " is-on" : "")}
                              {...(reduce ? {} : { whileHover: { y: -2 }, whileTap: { scale: 0.99 } })}
                            >
                              <input
                                type="radio"
                                name="ld5-company"
                                className="ld5-sr"
                                value={c.companyId}
                                checked={isOn}
                                onChange={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                              />
                              <Card className="ld5-co-card">
                                <span className="ld5-av" aria-hidden="true">
                                  {(c.companyName || "?").trim().charAt(0).toUpperCase()}
                                </span>
                                <span className="ld5-conm">{c.companyName}</span>
                                <span className="ld5-tick" aria-hidden="true"><CheckIcon /></span>
                              </Card>
                            </motion.label>
                          );
                        })}
                      </motion.div>

                      {errorAlert}

                      <motion.div {...slide(0.22)}>
                        <MotionButton
                          type="button"
                          size="lg"
                          className="ld5-go"
                          onClick={() => FN_SelectCompany()}
                          {...press}
                        >
                          Continue to Dashboard
                          <ArrowForwardIcon sx={{ fontSize: 20 }} />
                        </MotionButton>
                      </motion.div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <p className="ld5-foot">
          <CopyrightIcon />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </p>
      </main>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld5-font"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld5-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(3px)", bgcolor: "rgba(2,28,21,0.55)" } },
          paper: {
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              backgroundImage: "none",
              m: { xs: 2, sm: 4 },
              width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
              maxWidth: { xs: "calc(100% - 32px)", sm: 444 },
              maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" },
            },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="ld5-font">
            {/* Top banner */}
            <div className="ld5-sbanner">
              <div className="ld5-sbtop">
                <span className="ld5-sicon" aria-hidden="true">
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </span>
                <div>
                  <h2 className="ld5-stitle" id="ld5-session-title">Session Limit Reached</h2>
                  <p className="ld5-ssub">
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </p>
                </div>
              </div>
              <p className="ld5-snote">Sign out of one of the devices below to log in on this device.</p>
            </div>

            {/* Device list */}
            <div className="ld5-slist">
              <h3 className="ld5-sh">Active Sessions</h3>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <motion.div
                  key={session.id}
                  initial={reduce ? false : { opacity: 1, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.1 + idx * 0.07, ease: EASE }}
                  {...(reduce ? {} : { whileHover: { y: -1 } })}
                >
                  <Card className="ld5-sess">
                    {/* Icon */}
                    <span className="ld5-sdev" aria-hidden="true">{deviceIcon(session.device_type)}</span>

                    {/* Info */}
                    <div className="ld5-sinfo">
                      <p className="ld5-sname">{session.device_name || "Unknown Device"}</p>
                      <p className="ld5-smeta">
                        <AccessTimeIcon aria-hidden="true" />
                        <span>{timeAgo(session.last_activity)}</span>
                        {session.ip_address && <span>· {session.ip_address}</span>}
                      </p>
                    </div>

                    {/* Action */}
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      className="ld5-kick"
                      disabled={kickingSessionId === session.id}
                      onClick={async () => {
                        setKickingSessionId(session.id);
                        setSessionPickerData(null);
                        await FN_HandleLogin(identifier, userPassword, session.id);
                        setKickingSessionId(null);
                      }}
                    >
                      {kickingSessionId === session.id
                        ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                        : "Sign Out"}
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Cancel */}
            <div className="ld5-sact">
              <Button type="button" variant="outline" className="ld5-scancel" onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>
    </ThemeScope>
  );
}

export default LoginDesign05;
